import React, { useState } from 'react';

const UserInviteForm = ({ 
  onSubmit, 
  onCancel, 
  teams = [], 
  availableScripts = {}, 
  initialRole = 'basic',
  showRoleSelector = true,
  showTeamSelector = true,
  submitLabel = 'Invite User',
  currentUserRole = 'basic',
  disabled = false,
  disabledMessage = 'Cannot invite users at this time'
}) => {
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState(() => {
    // Set appropriate initial role based on current user's permissions
    if (currentUserRole === 'admin') {
      return initialRole; // Super Admin can assign any role
    } else if (currentUserRole === 'team_lead') {
      return 'basic';
    } else {
      return 'basic'; // Default fallback
    }
  });
  const [selectedTeam, setSelectedTeam] = useState('');
  // Initialize to first available template, or 'example-script' as fallback
  const [selectedTemplate, setSelectedTemplate] = useState(() => {
    const templateIds = Object.keys(availableScripts).filter(id => id !== 'default');
    return templateIds.length > 0 ? templateIds[0] : 'example-script';
  });

  // Email normalization handler - prevents case sensitivity bugs
  const handleEmailChange = (e) => {
    const normalizedEmail = e.target.value.toLowerCase().trim();
    setUserEmail(normalizedEmail);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (userEmail.trim() && (!showTeamSelector || selectedTeam)) {
      const inviteData = {
        email: userEmail.toLowerCase().trim(), // Force lowercase to prevent case mismatches
        role: userRole,
        teamId: selectedTeam,
        template: selectedTemplate
      };
      console.log('📧 UserInviteForm submitting:', inviteData);
      console.log('📧 Selected template:', selectedTemplate, '→', availableScripts[selectedTemplate]?.name || 'Unknown');
      onSubmit(inviteData);
      setUserEmail('');
      setUserRole(() => {
        if (currentUserRole === 'admin') {
          return initialRole;
        } else if (currentUserRole === 'team_lead') {
          return 'basic';
        } else {
          return 'basic';
        }
      });
      setSelectedTeam('');
      // Reset to first available template
      const templateIds = Object.keys(availableScripts).filter(id => id !== 'default');
      setSelectedTemplate(templateIds.length > 0 ? templateIds[0] : 'example-script');
    }
  };

  const inputCls = disabled
    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
    : 'border-gray-300 focus:ring-green-500';

  return (
    <div className={`p-3 rounded-md border ${disabled ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200'}`}>
      <h4 className="text-sm font-medium text-gray-700 mb-2">Invite User</h4>

      {disabled && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {disabledMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="flex flex-wrap gap-2">
          <input
            type="email"
            placeholder="User email address"
            value={userEmail}
            onChange={handleEmailChange}
            disabled={disabled}
            className={`flex-1 min-w-[200px] px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            autoFocus={!disabled}
          />

          {showRoleSelector && (
            <select
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              disabled={disabled}
              className={`px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            >
              <option value="basic">Agent</option>
              {currentUserRole === 'admin' && <option value="team_lead">Team Lead</option>}
              {currentUserRole === 'admin' && <option value="admin">Super Admin</option>}
            </select>
          )}
        </div>

        {showTeamSelector && (
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
            disabled={disabled}
            className={`w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 ${inputCls}`}
          >
            <option value="">Select Team</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-0.5">
              Script Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              disabled={disabled}
              className={`w-full px-3 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 ${inputCls}`}
            >
              {Object.entries(availableScripts)
                .filter(([id]) => id !== 'default')
                .sort((a, b) => {
                  if (a[0] === 'example-script') return 1;
                  if (b[0] === 'example-script') return -1;
                  return (a[1].name || '').localeCompare(b[1].name || '');
                })
                .map(([id, script]) => (
                  <option key={id} value={id}>
                    {script.name || id}
                  </option>
                ))}
            </select>
          </div>

          <button
            type="submit"
            disabled={disabled || !userEmail.trim() || (showTeamSelector && !selectedTeam)}
            className="px-4 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {submitLabel}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-1.5 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default UserInviteForm;

